import { getKeycloakConfig, KEYCLOAK_CONFIG } from '../config/authConfig';

export const CAPABILITY_CODES = {
  RTU_MASTER_READ_ALL: 'RTU_MASTER_READ_ALL',
  FEATURE_BETA_ACCESS: 'FEATURE_BETA_ACCESS',
  INTERNAL_OBSERVABILITY_ACCESS: 'INTERNAL_OBSERVABILITY_ACCESS',
} as const;

const CLIENT_ROLE_TO_CAPABILITY: Record<string, string> = {
  dms_rtu_master_read_all: CAPABILITY_CODES.RTU_MASTER_READ_ALL,
  dms_feature_beta_access: CAPABILITY_CODES.FEATURE_BETA_ACCESS,
  dms_internal_observability_access: CAPABILITY_CODES.INTERNAL_OBSERVABILITY_ACCESS,
};

type CapabilityCode = (typeof CAPABILITY_CODES)[keyof typeof CAPABILITY_CODES];

type CapabilityUser = {
  capabilities?: unknown[];
  roles?: unknown[];
};

type TokenParsed = {
  realm_access?: {
    roles?: unknown[];
  };
  resource_access?: Record<string, { roles?: unknown[] }>;
  capabilities?: unknown[];
};

const normalize = (value: unknown): string => String(value ?? '').trim().toUpperCase();

const uniqueStrings = (values: readonly unknown[]): string[] => {
  const unique = new Set<string>();
  for (const value of values) {
    const normalized = String(value ?? '').trim();
    if (normalized) {
      unique.add(normalized);
    }
  }
  return Array.from(unique);
};

const uniqueNormalized = (values: readonly unknown[]): string[] => {
  const unique = new Set<string>();
  for (const value of values) {
    const normalized = normalize(value);
    if (normalized) {
      unique.add(normalized);
    }
  }
  return Array.from(unique);
};

export const extractCapabilitiesFromAuthUser = <T extends CapabilityUser>(user: T | null | undefined): string[] =>
  uniqueNormalized(user?.capabilities ?? []);

export const extractRolesFromAuthUser = <T extends CapabilityUser>(user: T | null | undefined): string[] =>
  uniqueNormalized(user?.roles ?? []);

export const extractCapabilitiesFromToken = (tokenParsed: TokenParsed | null | undefined): string[] => {
  if (!tokenParsed) {
    return [];
  }

  const directCapabilities = uniqueNormalized(tokenParsed.capabilities ?? []);
  const configuredClientId = getKeycloakConfig().clientId;
  const candidateClientIds = uniqueStrings([
    configuredClientId,
    // Legacy deployments used this client id before the shared DMS client was adopted.
    'dms-frontend',
    KEYCLOAK_CONFIG.local.clientId,
    KEYCLOAK_CONFIG.staging.clientId,
    KEYCLOAK_CONFIG.production.clientId,
  ]);
  const clientRoles = candidateClientIds.flatMap((clientId) => tokenParsed.resource_access?.[clientId]?.roles ?? []);
  const mappedClientCapabilities = clientRoles
    .map((role) => CLIENT_ROLE_TO_CAPABILITY[String(role)] ?? null)
    .filter((value): value is string => value !== null);

  return uniqueNormalized([...directCapabilities, ...mappedClientCapabilities]);
};

export const extractRealmRolesFromToken = (tokenParsed: TokenParsed | null | undefined): string[] =>
  uniqueNormalized(tokenParsed?.realm_access?.roles ?? []);

export const hasCapabilityCode = (capabilities: string[] | undefined, capabilityCode: CapabilityCode): boolean => {
  const expected = normalize(capabilityCode);
  return (capabilities ?? []).some((value) => normalize(value) === expected);
};

export const hasAdminRole = (roles: string[] | undefined): boolean => {
  const normalizedRoles = uniqueNormalized(roles ?? []);
  return normalizedRoles.includes('ADMIN') || normalizedRoles.includes('ROLE_ADMIN');
};
